import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { API, useAuth } from "@/App";
import { logError } from "@/lib/logger";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FileUpload } from "../FileUpload";
import {
  Image,
  Gift,
  Trash2,
  Plus,
  X,
  Upload,
} from "lucide-react";

export const WishlistManager = () => {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ 
    name: "", description: "", url: "", image_url: "", price_range: "$", category: "gifts" 
  });

  const fetchItems = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/public/wishlist`);
      setItems(response.data);
    } catch (error) {
      logError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleImageUpload = (url) => {
    const fullUrl = url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
    setFormData({ ...formData, image_url: fullUrl });
  };

  const addItem = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/wishlist`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Item added!");
      setShowForm(false);
      setFormData({ name: "", description: "", url: "", image_url: "", price_range: "$", category: "gifts" });
      fetchItems();
    } catch (error) {
      toast.error("Failed to add item");
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await axios.delete(`${API}/admin/wishlist/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Item deleted");
      fetchItems();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid="admin-wishlist-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl text-charcoal">Wishlist / Gifts</h1>
          <p className="font-body text-stone-grey">Manage your wishlist items</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} className="btn-primary">
          <Plus className="w-4 h-4 mr-2" /> Add Item
        </Button>
      </div>

      {showForm && (
        <div className="glass rounded-xl p-6 mb-8">
          <form onSubmit={addItem} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="font-body text-sm text-stone-grey mb-2 block">Item Name</Label>
                <Input 
                  value={formData.name} 
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="La Mer Moisturizer"
                  required
                  className="form-input"
                />
              </div>
              <div>
                <Label className="font-body text-sm text-stone-grey mb-2 block">Category</Label>
                <select 
                  value={formData.category} 
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="form-input w-full"
                >
                  <option value="beauty">Beauty</option>
                  <option value="fashion">Fashion</option>
                  <option value="experiences">Experiences</option>
                  <option value="gifts">Gifts</option>
                </select>
              </div>
            </div>
            <div>
              <Label className="font-body text-sm text-stone-grey mb-2 block">Description</Label>
              <Input 
                value={formData.description} 
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="The iconic moisturizer for radiant skin"
                className="form-input"
              />
            </div>
            <div>
              <Label className="font-body text-sm text-stone-grey mb-2 block">Link URL (where to buy)</Label>
              <Input 
                value={formData.url} 
                onChange={(e) => setFormData({...formData, url: e.target.value})}
                placeholder="https://amazon.com/..."
                required
                className="form-input"
              />
            </div>
            <div>
              <Label className="font-body text-sm text-stone-grey mb-2 block">Item Image</Label>
              <FileUpload onUpload={handleImageUpload} label="Upload item image" />
              {formData.image_url && (
                <div className="mt-2 border rounded-lg p-2 bg-sand/20 flex items-center gap-4">
                  <img src={formData.image_url} alt="Preview" className="h-16 rounded" />
                  <Button type="button" variant="ghost" size="sm" onClick={() => setFormData({...formData, image_url: ""})}>
                    <X className="w-4 h-4" /> Remove
                  </Button>
                </div>
              )}
            </div>
            <div>
              <Label className="font-body text-sm text-stone-grey mb-2 block">Price Range</Label>
              <select 
                value={formData.price_range} 
                onChange={(e) => setFormData({...formData, price_range: e.target.value})}
                className="form-input w-full"
              >
                <option value="$">$ (Under $50)</option>
                <option value="$$">$$ ($50-150)</option>
                <option value="$$$">$$$ ($150-500)</option>
                <option value="$$$$">$$$$ ($500+)</option>
              </select>
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="btn-primary">Add Item</Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : items.length === 0 ? (
        <div className="glass rounded-xl p-12 text-center">
          <Gift className="w-12 h-12 text-taupe mx-auto mb-4" />
          <p className="font-body text-stone-grey">No wishlist items yet. Add your favorites!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((item) => (
            <div key={item.id} className="glass rounded-xl p-4 flex items-center gap-4">
              {item.image_url && (
                <img src={item.image_url} alt={item.name} className="w-16 h-16 object-cover rounded" />
              )}
              <div className="flex-1">
                <h3 className="font-heading text-lg text-charcoal">{item.name}</h3>
                <p className="font-body text-xs text-taupe">{item.category} • {item.price_range}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => deleteItem(item.id)}>
                <Trash2 className="w-4 h-4 text-lipstick-red" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

